import { ReactElement, useEffect, useState } from 'react';
import Image from 'next/image';
import { Toggle } from '@appsumo/dorado-react';

import { useCart } from '~/lib/cart';
import { cartContainsPlusDeal } from '~/lib/checkout/util';
import { currencyWithCents } from '~/lib/format';
import { useCheckoutState, useCheckoutAPI } from '~/hooks/checkout';
import { STEP } from '~/constants/checkout';

import credits from '~/public/credits.svg';
import clsx from 'clsx';

export default function PaymentOption({
  disablePM,
  isPaymentPending,
}: Readonly<{
  disablePM: boolean;
  isPaymentPending: boolean;
}>): ReactElement {
  const { toggleCredits } = useCart();
  const [isToggled, setIsToggled] = useState(false);
  const [isCreditsPending, setIsCreditsPending] = useState(false);
  const { cart } = useCheckoutState();
  const { setStepErrorMessage } = useCheckoutAPI();

  useEffect(() => {
    setIsToggled(!!cart?.use_credit);
  }, [cart]);

  const creditAmountText = (): string => {
    if (!cart) return '';

    if (cart.use_credit) {
      return `${currencyWithCents(cart.credit)} applied!`;
    }

    return `${currencyWithCents(cart.available_credit)} available!`;
  };

  const applyCredits = async (isCredits: boolean): Promise<void> => {
    if (isPaymentPending) return;

    setStepErrorMessage(STEP.PAYMENT, '');
    setIsToggled(isCredits);
    setIsCreditsPending(true);

    const [, updatedCart] = await toggleCredits(isCredits);

    setIsCreditsPending(false);

    if (!updatedCart) {
      setStepErrorMessage(
        STEP.PAYMENT,
        `Unable to ${isCredits ? 'add' : 'remove'} credits. Please try again.`,
      );
      setIsToggled(isToggled); // NOSONAR - This is intentional, we need to reset the state in case of failure

      return;
    }

    setIsToggled(updatedCart?.use_credit);
  };

  const toggleClass = clsx('ml-auto', {
    'pointer-events-none select-none opacity-50': isPaymentPending,
    'pointer-events-none select-none': isCreditsPending,
  });

  if (
    !cart?.available_credit ||
    (cartContainsPlusDeal(cart) && cart?.cart_items?.length === 1)
  )
    return <></>;

  return (
    <>
      <div className="mt-4 flex w-full items-center">
        <Image
          src={credits}
          alt="credits-icon"
          className="mr-4 h-6 w-6"
          height={24}
          width={24}
        />
        <div className="flex flex-col">
          <p>Apply AppSumo credits</p>
          <p className="font-bold text-money-moss">{creditAmountText()}</p>
        </div>

        <div className={toggleClass}>
          <Toggle onToggle={applyCredits} checked={isToggled} />
        </div>
      </div>
      {/* Match opacity of other lines if credits are disabled */}
      <div
        className="h-[1px] border-b border-sundog pb-6"
        style={{ opacity: disablePM ? '50%' : '100%' }}
      />
    </>
  );
}
